import { useEffect, useRef, useState } from "react";

import { ApiError } from "@/lib/api";
import { env } from "@/lib/env";
import { promptGoogleOneTap, renderGoogleButton } from "@/lib/google-identity";
import { useAdminAuth } from "@/hooks/use-admin-auth";

type AdminGoogleSignInProps = {
  /** Blurred-admin overlay: classic One Tap over the page; button only if unavailable. */
  overlay?: boolean;
};

type PromptUi = "one_tap" | "fallback" | "hidden";

const FALLBACK_DELAY_MS = 500;

function toErrorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Google sign-in failed.";
}

export function AdminGoogleSignIn({ overlay = false }: AdminGoogleSignInProps) {
  const { loginWithGoogle } = useAdminAuth();
  const buttonRef = useRef<HTMLDivElement>(null);
  const startedRef = useRef(false);
  const mountedRef = useRef(true);
  const fallbackTimerRef = useRef<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [promptUi, setPromptUi] = useState<PromptUi>(
    overlay ? "hidden" : "fallback",
  );

  function clearFallbackTimer() {
    if (fallbackTimerRef.current !== null) {
      window.clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
  }

  useEffect(() => {
    mountedRef.current = true;

    function showOneTapOnly() {
      clearFallbackTimer();
      setError(null);
      setPromptUi("one_tap");
    }

    function showFallbackOnly() {
      clearFallbackTimer();
      // Defer so a late "displayed" notification can cancel the fallback and
      // prevent both UIs from stacking (fallback behind One Tap).
      fallbackTimerRef.current = window.setTimeout(() => {
        if (!mountedRef.current) return;
        setPromptUi("fallback");
      }, FALLBACK_DELAY_MS);
    }

    async function handleCredential(idToken: string) {
      setIsSubmitting(true);
      setError(null);
      try {
        await loginWithGoogle(idToken);
      } catch (err) {
        clearFallbackTimer();
        setPromptUi("fallback");
        setError(toErrorMessage(err));
      } finally {
        setIsSubmitting(false);
      }
    }

    async function setup() {
      // Prevent React StrictMode double-mount from issuing two prompts.
      if (startedRef.current) return;
      startedRef.current = true;

      if (!env.googleClientId) {
        clearFallbackTimer();
        setPromptUi("fallback");
        setError(
          "Google sign-in is not configured. Set VITE_GOOGLE_CLIENT_ID (same value as backend GOOGLE_CLIENT_ID).",
        );
        return;
      }

      try {
        await promptGoogleOneTap({
          onCredential: handleCredential,
          onError: (message) => {
            if (!mountedRef.current) return;
            clearFallbackTimer();
            setPromptUi("fallback");
            setError(message);
          },
          onPromptDisplayed: () => {
            if (!mountedRef.current) return;
            showOneTapOnly();
          },
          onPromptUnavailable: () => {
            if (!mountedRef.current) return;
            showFallbackOnly();
          },
        });

        if (!overlay && buttonRef.current && mountedRef.current) {
          await renderGoogleButton(buttonRef.current, {
            onCredential: handleCredential,
            onError: (message) => {
              if (mountedRef.current) setError(message);
            },
          });
        }
      } catch (err) {
        if (!mountedRef.current) return;
        startedRef.current = false;
        clearFallbackTimer();
        setPromptUi("fallback");
        setError(
          err instanceof Error
            ? err.message
            : "Unable to start Google sign-in.",
        );
      }
    }

    void setup();

    return () => {
      mountedRef.current = false;
      clearFallbackTimer();
      // Keep startedRef=true so StrictMode remount does not prompt twice.
    };
  }, [loginWithGoogle, overlay]);

  useEffect(() => {
    if (
      !overlay ||
      promptUi !== "fallback" ||
      !buttonRef.current ||
      !env.googleClientId
    ) {
      return;
    }

    let active = true;

    void renderGoogleButton(buttonRef.current, {
      onCredential: async (idToken) => {
        setIsSubmitting(true);
        setError(null);
        try {
          await loginWithGoogle(idToken);
        } catch (err) {
          setError(toErrorMessage(err));
        } finally {
          setIsSubmitting(false);
        }
      },
      onError: (message) => {
        if (active) setError(message);
      },
    });

    return () => {
      active = false;
    };
  }, [overlay, promptUi, loginWithGoogle]);

  if (overlay) {
    // Render fallback only when explicitly selected — never alongside One Tap.
    if (promptUi !== "fallback") {
      return null;
    }

    return (
      <div className="pointer-events-none absolute inset-0 z-50">
        <div className="pointer-events-auto absolute right-4 top-4 w-full max-w-xs rounded-lg border border-slate-200 bg-white/95 p-4 shadow-lg backdrop-blur dark:border-slate-700 dark:bg-slate-900/95 sm:right-6 sm:top-6">
          <p className="text-sm font-medium text-slate-900 dark:text-slate-50">
            Sign in to continue
          </p>
          <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
            Use your allowlisted Google account.
          </p>
          <div ref={buttonRef} className="mt-3 flex min-h-10 justify-center" />
          {isSubmitting ? (
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              Completing sign-in…
            </p>
          ) : null}
          {error ? (
            <p
              className="mt-2 text-xs text-red-600 dark:text-red-400"
              role="alert"
            >
              {error}
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <section className="w-full max-w-md space-y-6 rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="space-y-2">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
          Admin sign in
        </h1>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Sign in with your allowlisted Google account. One Tap will appear
          automatically when available.
        </p>
      </div>

      <div ref={buttonRef} className="flex min-h-10 justify-center" />

      {isSubmitting ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Completing sign-in…
        </p>
      ) : null}

      {error ? (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}
